import React, { Component } from "react";
import * as Icon from "react-feather";
import FaqService from "../../services/FaqService";
import { RichTextEditor } from "../common/rich_text_editor";

class FaqAddModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            question: "",
            videoTitle: "",
            category: "",
            transcriptText: "",
            content: "",       // rich HTML for the answer body (text + images)
            images: [],        // canonical list of uploaded images: [{url, name}]
            videoFile: null,
            videoFileName: "",
            saving: false,
            error: null,
        };
        this.fileInputRef = React.createRef();
    }

    onContentChange = (html) => {
        this.setState({ content: html, error: null });
    };

    /**
     * Called by RichTextEditor when the user inserts an image. Uploads to S3
     * and returns the URL so the editor can insert <img src=…> at the cursor.
     */
    onImageUpload = async (file) => {
        const { url } = await FaqService.uploadImage(file);
        return url;
    };

    onImageUploaded = (img) => {
        this.setState({ images: [...this.state.images, img] });
    };

    onFieldChange = (e) => {
        this.setState({ [e.target.name]: e.target.value, error: null });
    };

    onVideoFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            this.setState({ videoFile: file, videoFileName: file.name, error: null });
        }
    };

    onRemoveVideo = () => {
        this.setState({ videoFile: null, videoFileName: "" });
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = "";
        }
    };

    onSubmit = async () => {
        let { question, videoTitle, category, transcriptText, content, images, videoFile } = this.state;

        if (!question.trim()) {
            this.setState({ error: "Question is required." });
            return;
        }
        if (!transcriptText.trim()) {
            this.setState({ error: "Transcript text is required." });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            await FaqService.create({
                question: question.trim(),
                videoTitle: videoTitle.trim() || null,
                category: category.trim() || null,
                transcriptText: transcriptText.trim(),
                content: content || null,
                images,
                videoFile,
            });

            this.props.onSaved();
        } catch (error) {
            console.error(error);
            this.setState({
                saving: false,
                error: error.message || "Failed to create FAQ. Please try again.",
            });
        }
    };

    onKeyDown = (e) => {
        if (e.key === "Escape") {
            this.props.onClose();
        }
    };

    onOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            this.props.onClose();
        }
    };

    render() {
        let { question, videoTitle, category, transcriptText, content, videoFileName, saving, error } = this.state;

        return (
            <div className="faq-modal-overlay" onClick={this.onOverlayClick} onKeyDown={this.onKeyDown}>
                <div className="faq-modal" role="dialog" aria-modal="true" aria-labelledby="faq-modal-title">
                    <div className="faq-modal__header">
                        <h2 id="faq-modal-title" className="faq-modal__title">
                            <Icon.PlusCircle size={18} style={{ marginRight: 8 }} />
                            Add FAQ
                        </h2>
                        <button className="faq-modal__close-btn" onClick={this.props.onClose} aria-label="Close">
                            <Icon.X size={18} />
                        </button>
                    </div>

                    <div className="faq-modal__body">
                        {error && (
                            <div className="faq-modal__error">
                                <Icon.AlertCircle size={14} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="faq-modal__field">
                            <label className="faq-modal__label">
                                Question <span className="faq-modal__required">*</span>
                            </label>
                            <input
                                className="faq-modal__input"
                                type="text"
                                name="question"
                                value={question}
                                onChange={this.onFieldChange}
                                placeholder="e.g. How do I create a new engagement?"
                                disabled={saving}
                            />
                        </div>

                        <div className="faq-modal__row">
                            <div className="faq-modal__field">
                                <label className="faq-modal__label">Video Title</label>
                                <input
                                    className="faq-modal__input"
                                    type="text"
                                    name="videoTitle"
                                    value={videoTitle}
                                    onChange={this.onFieldChange}
                                    placeholder="e.g. Creating Engagements"
                                    disabled={saving}
                                />
                            </div>

                            <div className="faq-modal__field">
                                <label className="faq-modal__label">Category</label>
                                <input
                                    className="faq-modal__input"
                                    type="text"
                                    name="category"
                                    value={category}
                                    onChange={this.onFieldChange}
                                    placeholder="e.g. Getting Started"
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        <div className="faq-modal__field">
                            <label className="faq-modal__label">
                                Transcript Text <span className="faq-modal__required">*</span>
                            </label>
                            <textarea
                                className="faq-modal__textarea"
                                name="transcriptText"
                                value={transcriptText}
                                onChange={this.onFieldChange}
                                placeholder="Paste the video transcript here. This will be indexed for AI search."
                                rows={6}
                                disabled={saving}
                            />
                            <div className="faq-modal__hint">
                                Used for AI search indexing. Not shown to readers.
                            </div>
                        </div>

                        <div className="faq-modal__field">
                            <label className="faq-modal__label">
                                Answer (rich text + images)
                            </label>
                            <RichTextEditor
                                value={content}
                                onChange={this.onContentChange}
                                onImageUpload={this.onImageUpload}
                                onImageUploaded={this.onImageUploaded}
                                placeholder="Type the answer the way you want readers to see it. Use the toolbar to add formatting and screenshots."
                                minHeight={220}
                                readOnly={saving}
                            />
                            <div className="faq-modal__hint">
                                Click the image button in the toolbar to attach screenshots. Images appear inline where your cursor is.
                            </div>
                        </div>

                        <div className="faq-modal__field">
                            <label className="faq-modal__label">Video File</label>
                            {videoFileName ? (
                                <div className="faq-modal__file-selected">
                                    <Icon.Video size={14} />
                                    <span className="faq-modal__file-name">{videoFileName}</span>
                                    <button
                                        className="faq-modal__file-remove"
                                        onClick={this.onRemoveVideo}
                                        disabled={saving}
                                        aria-label="Remove video"
                                    >
                                        <Icon.X size={13} />
                                    </button>
                                </div>
                            ) : (
                                <div className="faq-modal__file-upload">
                                    <input
                                        ref={this.fileInputRef}
                                        type="file"
                                        accept="video/*"
                                        onChange={this.onVideoFileChange}
                                        disabled={saving}
                                        className="faq-modal__file-input"
                                        id="faq-video-upload"
                                    />
                                    <label htmlFor="faq-video-upload" className="faq-modal__file-label">
                                        <Icon.Upload size={14} />
                                        <span>Choose video file</span>
                                    </label>
                                    <span className="faq-modal__file-hint">MP4, MOV, WebM (optional)</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="faq-modal__footer">
                        <button className="faq-modal__btn faq-modal__btn--cancel" onClick={this.props.onClose} disabled={saving}>
                            Cancel
                        </button>
                        <button className="faq-modal__btn faq-modal__btn--save" onClick={this.onSubmit} disabled={saving}>
                            {saving ? (
                                <>
                                    <span className="faq-modal__spinner" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Icon.Check size={14} />
                                    Save FAQ
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default FaqAddModal;
